import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sunrise } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtINR } from "@/lib/documents";

/**
 * EARLY-CHECKIN · quick-add prompt shown right after a successful check-in.
 * `stays` = [{ stay_id, folio_id, stay_code, room_number, guest_name }] — the charge is posted to EVERY folio listed
 * (group check-in: one prompt, one charge per checked-in room). `onClose(posted: boolean)` fires on Post or Skip.
 */
export function EarlyCheckinPrompt({ open, stays = [], onClose }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Early check-in");
  const [taxRate, setTaxRate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setAmount(""); setDescription("Early check-in"); setTaxRate(""); setBusy(false); } }, [open]);

  const targets = stays.filter((s) => s?.folio_id);
  const amt = Number(amount);

  const post = async () => {
    if (!(amt > 0)) { toast.error("Enter the early check-in charge amount"); return; }
    if (!description.trim()) { toast.error("Enter a description"); return; }
    setBusy(true);
    let ok = 0;
    for (const s of targets) {
      try {
        await api.post(`/pms/folios/${s.folio_id}/charge`, {
          source_module: "early_checkin", description: description.trim(), quantity: 1, unit_price: amt,
          tax_rate: taxRate !== "" ? Number(taxRate) : null, notes: `Posted at check-in · ${s.stay_code || ""}`.trim(),
        });
        ok += 1;
      } catch (e) {
        toast.error(`Room ${s.room_number || ""}: ${formatApiError(e)}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(ok === 1 ? `Early check-in charge ${fmtINR(amt)} posted to the folio` : `Early check-in charge ${fmtINR(amt)} posted to ${ok} folios`);
    onClose?.(ok > 0);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(false); }}>
      <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[480px]" data-hocp-dialog data-testid="early-checkin-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sunrise size={18} strokeWidth={1.5} />Early check-in charge?</DialogTitle>
          <DialogDescription>
            {targets.length > 1
              ? `Guest checked in to ${targets.length} rooms. Post an early check-in charge to each room's folio, or skip.`
              : `${targets[0]?.guest_name || "Guest"} is checked in${targets[0]?.room_number ? ` to room ${targets[0].room_number}` : ""}. Post an early check-in charge to the folio, or skip.`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-hocp-textMuted">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={120} data-testid="early-checkin-description" />
          </div>
          <div>
            <Label className="text-[12px] text-hocp-textMuted">Amount (₹){targets.length > 1 ? " per room" : ""}</Label>
            <Input type="number" min={0} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 1000" className="mono" data-testid="early-checkin-amount" />
          </div>
          <div>
            <Label className="text-[12px] text-hocp-textMuted">GST % <span className="opacity-70">(optional)</span></Label>
            <Input type="number" min={0} max={100} step="0.5" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="e.g. 12" className="mono" data-testid="early-checkin-tax" />
          </div>
        </div>
        {targets.length > 1 && (
          <div className="text-[12px] text-hocp-textMuted" data-testid="early-checkin-rooms">
            Rooms: {targets.map((s) => s.room_number || s.stay_code).join(", ")}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.(false)} disabled={busy} data-testid="early-checkin-skip">Skip</Button>
          <Button onClick={post} disabled={busy || !(amt > 0)} data-testid="early-checkin-post">
            {busy ? "Posting…" : `Post charge${amt > 0 ? ` · ${fmtINR(amt)}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
